import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { hasPremium } from "../lib/plan";
import { Mic, LayoutDashboard, UserCircle, LogOut, ShieldCheck, ScanEye, MoreHorizontal, BrainCircuit, CandlestickChart, Trophy } from "lucide-react";
import { NotificationBell } from "./NotificationBell";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "./ui/dropdown-menu";

const PRIMARY_NAV = [
  { to: "/", label: "Assistante", icon: Mic, end: true, testid: "nav-assistant" },
  { to: "/tableau-de-bord", label: "Tableau de bord", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/memoire", label: "Mémoire", icon: BrainCircuit, testid: "nav-memory" },
  { to: "/compte", label: "Compte", icon: UserCircle, testid: "nav-account" },
];
const SECONDARY_NAV = [
  { to: "/trading", label: "Trading", icon: CandlestickChart, testid: "nav-trading", premium: true },
  { to: "/vision", label: "Vision", icon: ScanEye, testid: "nav-vision", premium: true },
  { to: "/football", label: "Football", icon: Trophy, testid: "nav-football", premium: true },
];
const ADMIN_LINK = { to: "/admin", label: "Admin", icon: ShieldCheck, testid: "nav-admin" };

const pillClass = (isActive) =>
  `shrink-0 flex items-center gap-2 px-3.5 py-2 clip-corner font-mono-hud text-[10px] sm:text-[11px] tracking-[0.15em] uppercase transition-all ${
    isActive
      ? "bg-[var(--accent-a25)] text-white border border-[var(--accent)] shadow-[var(--accent-glow)]"
      : "text-[#8b949e] border border-[var(--border-hud)] hover:text-white hover:bg-[var(--accent-a10)]"
  }`;

export function AppShell({ title, subtitle, children, wide = false }) {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  const premium = hasPremium(user);
  const primary = PRIMARY_NAV.filter((n) => !n.premium || premium);
  const base = SECONDARY_NAV.filter((n) => !n.premium || premium);
  const secondary = user?.role === "admin" ? [...base, ADMIN_LINK] : base;
  const isSecondaryActive = (to) => pathname === to || pathname.startsWith(to + "/");
  const moreActive = secondary.some((n) => isSecondaryActive(n.to));

  return (
    <div className="min-h-screen flex flex-col">
      <header className="relative z-30 flex items-center justify-between gap-3 px-4 sm:px-6 py-3 border-b border-[var(--border-hud)] bg-[rgba(4,10,22,0.6)] backdrop-blur-md">
        <div className="flex items-center gap-2.5 min-w-0">
          <span className="h-2.5 w-2.5 rounded-full shrink-0 accent-glow" style={{ background: "var(--accent)" }} />
          <h1 className="font-display text-base sm:text-lg font-black tracking-[0.18em] text-glow accent-text truncate">
            SARA CONNOR
          </h1>
        </div>
        <div className="flex items-center gap-2.5">
          <span className="hidden md:block font-mono-hud text-[11px] text-[#8b949e] tracking-wide truncate max-w-[160px]">
            {user?.name}
          </span>
          <NotificationBell />
          <button
            data-testid="shell-logout-button"
            onClick={logout}
            aria-label="Déconnexion"
            className="hud-btn h-9 w-9 flex items-center justify-center clip-corner"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </header>

      <nav className="relative z-20 flex flex-wrap items-center gap-1.5 px-3 sm:px-6 py-2.5 border-b border-[var(--border-hud)] bg-[rgba(3,8,18,0.5)] backdrop-blur-md">
        {primary.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            data-testid={n.testid}
            className={({ isActive }) => pillClass(isActive)}
          >
            <n.icon className="h-4 w-4" />
            {n.label}
          </NavLink>
        ))}

        {secondary.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button type="button" data-testid="nav-more-trigger" className={`relative ${pillClass(moreActive)}`}>
              <MoreHorizontal className="h-4 w-4" />
              Plus
              {moreActive && (
                <span
                  data-testid="nav-more-active-dot"
                  aria-label="Une page de ce menu est active"
                  className="absolute -top-1 -right-1 h-2 w-2 rounded-full animate-pulse"
                  style={{ background: "var(--accent)", boxShadow: "0 0 8px 2px var(--accent)" }}
                />
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            data-testid="nav-more-menu"
            className="min-w-[210px] p-1.5 bg-[rgba(4,10,22,0.97)] backdrop-blur-md border border-[var(--border-hud)] shadow-[var(--accent-glow)]"
          >
            {secondary.map((n) => {
              const active = isSecondaryActive(n.to);
              return (
                <DropdownMenuItem
                  key={n.to}
                  asChild
                  className="clip-corner cursor-pointer focus:bg-[var(--accent-a10)] focus:text-white"
                >
                  <NavLink
                    to={n.to}
                    data-testid={n.testid}
                    className={`flex items-center gap-2.5 px-2 py-1.5 font-mono-hud text-[11px] tracking-[0.14em] uppercase transition-colors ${
                      active ? "accent-text" : "text-[#8b949e]"
                    }`}
                  >
                    <n.icon className="h-4 w-4" />
                    {n.label}
                  </NavLink>
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
        )}
      </nav>

      <main className={`flex-1 px-4 sm:px-6 py-6 sm:py-8 w-full mx-auto ${wide ? "max-w-[1700px]" : "max-w-6xl"}`}>
        {title && (
          <div className="mb-7 animate-rise">
            <h2 className="font-display text-2xl sm:text-3xl font-black tracking-[0.12em] text-glow accent-text">
              {title}
            </h2>
            {subtitle && (
              <p className="mt-1.5 font-mono-hud text-[11px] tracking-[0.2em] text-[#8b949e] uppercase">
                {subtitle}
              </p>
            )}
          </div>
        )}
        {children}
      </main>

      <style>{`.no-scrollbar::-webkit-scrollbar{display:none}.no-scrollbar{-ms-overflow-style:none;scrollbar-width:none}`}</style>
    </div>
  );
}
